import * as React from 'react';
import { useState } from 'react';
import { Drawer } from 'antd';
import withStyles from 'react-jss';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AskAiDrawerProps, ReduxStateProps } from './AskAiDrawer/types';
import { styles } from './AskAiDrawer/styles';
import ChatHeader from './AskAiDrawer/ChatHeader';
import MessageItem from './AskAiDrawer/MessageItem';
import LoadingIndicator from './AskAiDrawer/LoadingIndicator';
import SuggestionsPanel from './AskAiDrawer/SuggestionsPanel';
import ChatInput from './AskAiDrawer/ChatInput';

import {
  useAgentDetection,
  useChatState,
  useMessageHandling,
  useScrollToBottom,
  useIframeMessageListener
} from './AskAiDrawer/hooks';

const AskAiDrawer: React.FC<AskAiDrawerProps> = ({ 
  visible, 
  onClose, 
  classes, 
  t,
  analyticsContext
}) => {

  const [inputValue, setInputValue] = useState('');
  
  // Custom hooks for state management
  const { selectedAgent, setSelectedAgent, showBI } = useAgentDetection();
  const { 
    messages, 
    isLoading, 
    currentLayerIndex,
    addMessageToChat, 
    setLoadingState, 
    clearChatMessages, 
    toggleToolCallExpansion 
  } = useChatState();
  
  const { handleSendMessage } = useMessageHandling(
    selectedAgent, 
    analyticsContext, 
    addMessageToChat, 
    setLoadingState, 
    messages
  );
  const { messagesEndRef } = useScrollToBottom(messages);
  const { handleResetState } = useIframeMessageListener(clearChatMessages);

  // Event handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(undefined, inputValue);
      setInputValue('');
    }
  };

  const handleSendClick = () => {
    handleSendMessage(undefined, inputValue);
    setInputValue('');
  };

  const handleSuggestionClick = (query: string) => {
    handleSendMessage(query);
  };

  const handleClearChat = () => {
    clearChatMessages();
    handleResetState();
  };

  const drawerTitle = (
    <ChatHeader
      selectedAgent={selectedAgent}
      showBI={showBI}
      classes={classes}
      t={t}
      onAgentChange={setSelectedAgent}
      onClearChat={handleClearChat}
    />
  );

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      closable={true}
      onClose={onClose}
      visible={visible}
      className={classes.drawer}
      width={600}
    >
      <div className={classes.chatContainer}>
        <div className={classes.messageList}>
          {messages.length === 0 ? (
            <SuggestionsPanel
              selectedAgent={selectedAgent}
              classes={classes}
              t={t}
              onSuggestionClick={handleSuggestionClick}
            />
          ) : (
            <>
              {messages.map((message, index) => (
                <MessageItem
                  key={`${message.sender}-${index}-${message.timestamp.getTime()}`}
                  message={message}
                  messageIndex={index}
                  classes={classes}
                  onToggleToolCall={toggleToolCallExpansion}
                  currentLayerIndex={currentLayerIndex}
                />
              ))}
            </>
          )}
          {isLoading && (
            <LoadingIndicator
              classes={classes}
              selectedAgent={selectedAgent}
            />
          )}
          <div ref={messagesEndRef} />
        </div>
        <ChatInput
          inputValue={inputValue}
          isLoading={isLoading}
          classes={classes}
          t={t}
          onInputChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onSendMessage={handleSendClick}
        />
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state: any): ReduxStateProps => {
  return {
    analyticsContext: state.analyticsContext,
  };
}

export default withTranslation('translation')(
  withStyles(styles, { injectTheme: true })(
    connect(mapStateToProps)(AskAiDrawer)
  )
); 